import React from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { faTrash } from '@fortawesome/pro-light-svg-icons/faTrash';
import { TypedMemo } from '../../../common/types';
import { BlockSaveStatus } from './useBlockAutosave';
import { formMessages } from './formMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for several parts; each part uses its own slice of it */
import styles from './SMLBlockForm.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SMLBlockFormFootProps {
  status: BlockSaveStatus;
  /** The last save reached the live page by itself ("Saved and live"). */
  isLive: boolean;
  /** The "Not saved" words for this form ("Check the address."). */
  invalidMessage?: MessageDescriptor;
  onDelete: () => void;
  onDone: () => void;
}

function statusMessage(status: BlockSaveStatus, isLive: boolean, invalid: MessageDescriptor): MessageDescriptor | null {
  switch (status) {
    case 'saving':
      return formMessages.saving;
    case 'saved':
      return isLive ? formMessages.savedLive : formMessages.savedNotLive;
    case 'invalid':
      return invalid;
    case 'failed':
      return formMessages.saveFailed;
    case 'idle':
    default:
      return null;
  }
}

/** The foot of every block form (design d0, 8): the save state, Delete and Done. */
const SMLBlockFormFootComponent: React.FC<SMLBlockFormFootProps> = ({
  status,
  isLive,
  invalidMessage = formMessages.notSaved,
  onDelete,
  onDone,
}) => {
  const intl = useIntl();
  const message = statusMessage(status, isLive, invalidMessage);
  const isBad = status === 'invalid' || status === 'failed';

  return (
    <div className={styles.foot}>
      <span
        className={clsx(styles.saveState, isBad && styles.saveStateBad)}
        role="status"
        aria-live="polite"
        data-testid="block-save-state"
      >
        {status === 'saved' && <FontAwesomeIcon icon={faCheck} className={styles.saveIcon} />}
        {message === null ? null : intl.formatMessage(message)}
      </span>
      <button type="button" className={styles.deleteButton} onClick={onDelete} data-testid="block-form-delete">
        <FontAwesomeIcon icon={faTrash} />
        {intl.formatMessage(formMessages.delete)}
      </button>
      <button type="button" className={styles.doneButton} onClick={onDone} data-testid="block-form-done">
        {intl.formatMessage(formMessages.done)}
      </button>
    </div>
  );
};

export const SMLBlockFormFoot = TypedMemo(SMLBlockFormFootComponent);
